import Extension from "../base/extension";

class Facets extends Extension {
  form; // The HTML form element of the facets form
  filterData = []; // Cache of previous filter view requests, so we only fetch a given filter view once
  searchParamsInitial;
  searchParamsPrev;

  /**
   * ID of the section, so we know what section to request via fetch
   * @type {String}
   */
  sectionId;

  constructor(name) {
    super(name);
  }

  /**
   * Initialisation
   */
  init() {
    // Detect changes of radios within this element, as that means variant changed
    this.form = document.querySelector("#facets-form");
    if(!this.form) return;

    this.batchMode = this.form.hasAttribute('data-batch-apply');
    this.searchParamsInitial = window.location.search.slice(1);
    this.searchParamsPrev = window.location.search.slice(1);
    this.sectionId = document.querySelector("#products-container").dataset.sectionId;

    this.setupEventListeners();
  }

  /**
   * Setup event listeners
   */
  setupEventListeners() {
    // Listen for form input changes - i.e. the facet inputs. We call the
    // listener after a 'debounce' period to allow the user some time to
    // complete their input (rather than immediately on input)
    if (!this.batchMode) {
      this.debouncedOnInput = debounce((event) => {
        this.onFacetsFormInputChange(event);
      }, 500);
      this.form.addEventListener('input', this.debouncedOnInput.bind(this));
    }

    // Listen for 'facet remove' and 'facet apply' clicks. We delegate on the
    // container because buttons are re-rendered each time a filter is applied.
    this.form.addEventListener('click', function(event) {
      if(event.target.closest("a")?.classList.contains("facet-remove")){
        event.preventDefault();
        let mockedEvent = { currentTarget: event.target.closest("a"), preventDefault: () => {} };
        this.onActiveFilterClick(mockedEvent);
      }
      if(event.target.closest(".facet-apply")){
        event.preventDefault();
        this.onFacetsFormInputChange(event);
      }
    }.bind(this));
  }

  /**
   * Handle an input change in the facets form, and render the new results
   * @param {Event} e input event 
   */
  onFacetsFormInputChange(e) {
    const formData = new FormData(this.form);
    const searchParams = new URLSearchParams(formData).toString();
    this.renderPage(searchParams, e);
  }

  /**
   * Render the section
   * 
   * @param {String} searchParams The query string to load (filters / page)
   * @param {Event} event The original trigger event
   * @param {Boolean} updateURLHash Whether to update the URL hash with the new URL
   */
  renderPage(searchParams, event, updateURLHash = true) {
    this.searchParamsPrev = searchParams;
    document.getElementById('results-loading-faceted-results').classList.remove('d-none');
    const url = `${window.location.pathname}?section_id=${this.sectionId}&${searchParams}`;
    
    // Check if we have this combo of filters in the cache. If so, use cache. If
    // not, fetch fresh from Shopify.
    const filterDataUrl = element => element.url === url;
    this.filterData.some(filterDataUrl) ? 
      this.renderSectionFromCache(filterDataUrl, event) 
      : this.renderSectionFromFetch(url, event);

    if (updateURLHash) {
      this.updateURLHash(searchParams);
    }
  }

  /**
   * Render the section from previously-stored fetch (i.e. cache)
   * 
   * @param {String} filterDataUrl The URL we previously cached
   * @param {Event} event 
   */
  renderSectionFromCache(filterDataUrl, event) {
    const html = this.filterData.find(filterDataUrl).html;
    this.renderFilters(html, event);
    this.renderProductGridContainer(html);
    this.renderPagination(html);
    document.getElementById('results-loading-faceted-results').classList.add('d-none');
    flashtagLoader();
    Waypoint.refreshAll();
    var event = new CustomEvent("collection:filtered");
    document.dispatchEvent(event);
  }

  /**
   * Render the section by fetching it fresh
   * 
   * @param {String} url 
   * @param {Event} event 
   */
  renderSectionFromFetch(url, event) {
    fetch(url)
      .then(response => response.text())
      .then((responseText) => {
        const html = responseText;

        // Cache this response so we can load directly if the same options are
        // picked.
        this.filterData = [...this.filterData, { html, url }];

        this.renderFilters(html, event);
        this.renderProductGridContainer(html);
        this.renderPagination(html);
        document.getElementById('results-loading-faceted-results').classList.add('d-none');
        flashtagLoader();
        Waypoint.refreshAll();
        var event = new CustomEvent("collection:filtered");
        document.dispatchEvent(event);
      });
  }

  /**
   * Update the URL hash with new parameters
   * 
   * @param {String} searchParams 
   */
  updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  /**
   * Extract and replace the facet (filter dropdowns) HTML
   * 
   * @param {String} html
   * @param {Event} event
   */
  renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const facetLiveRegions = parsedHTML.querySelectorAll("[data-live-region^='facet-']");

    Array.from(facetLiveRegions).forEach((element) => {
      document.querySelector(`[data-live-region="${element.dataset.liveRegion}"]`).innerHTML = element.innerHTML;
    });

    // Update HTML of the 'active filters' section
    this.renderActiveFacets(parsedHTML);
  }

  /**
   * Extract and replace the active facet (filter badges) HTML
   * 
   * @param {String} html
   */
  renderActiveFacets(html) {
    const activeFacetElementSelectors = ['#facets-active'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      document.querySelector(selector).outerHTML = activeFacetsElement.outerHTML;
    })
    //FacetFiltersForm.toggleActiveFacets(false);
  }

  /**
   * Render the results products grid
   * 
   * @param {String} html
   */
  renderProductGridContainer(html) {
    document.getElementById('results-grid').innerHTML = new DOMParser().parseFromString(html, 'text/html').getElementById('results-grid').innerHTML;
  }

  /**
   * Extract and replace the pagination HTML
   * 
   * @param {String} html
   */
  renderPagination(html) {
    document.getElementById('pagination').outerHTML = new DOMParser().parseFromString(html, 'text/html').getElementById('pagination').outerHTML;
  }

  /**
   * Start the process of applying a filter
   * 
   * @param {Event} event The initiating event
   */
  onActiveFilterClick(event) {
    event.preventDefault();
    //this.toggleActiveFacets();
    const url = event.currentTarget.href.indexOf('?') == -1 ? '' : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    this.renderPage(url);
  }
}

export default Facets;